import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  ArrowRight,
  Users,
  Clock,
  CheckCircle,
  Trash2,
  Search,
  X,
  Star
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { config } from '@/src/config';
import { useTabbedChrome } from '@/components/weavein';

interface Member {
  id: string;
  name: string;
  phone: string;
  email: string;
  zipCode: string;
  dateOfBirth: string;
  medicaidId: string;
  healthPlan: string;
  lastActivity: string;
  status: 'Pending' | 'Matched' | 'Auto-Matched' | 'Accepted' | 'In Progress' | 'On Hold' | 'Completed';
  referralCount: number;
  referralIds: string[]; // Track all referral IDs for this member
  memberId?: number; // database ID for favorites
}

interface MemberProfilesProps {
  referrals: any[];
  onNavigateToWorkspace: (memberId: string) => void;
  onBack?: () => void;
  onDeleteReferral?: (referralId: string) => void;
}

export function MemberProfiles({ referrals, onNavigateToWorkspace, onBack, onDeleteReferral }: MemberProfilesProps) {
  const tabbed = useTabbedChrome();
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [favoriteIds, setFavoriteIds] = React.useState<Set<string>>(new Set());

  // Load favorites on mount
  React.useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id') || '1';
        const resp = await fetch(`${config.weaveinApi.baseUrl}/api/members/favorites/list`, {
          headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': cboId },
        });
        if (resp.ok) {
          const data = await resp.json();
          setFavoriteIds(new Set((data.favorite_member_ids || []).map(String)));
        }
      } catch {}
    };
    fetchFavorites();
  }, []);

  const toggleFavorite = async (memberId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id') || '1';
    const isFav = favoriteIds.has(memberId);
    const method = isFav ? 'DELETE' : 'POST';
    try {
      await fetch(`${config.weaveinApi.baseUrl}/api/members/favorites/${memberId}`, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': cboId },
      });
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (isFav) next.delete(memberId); else next.add(memberId);
        return next;
      });
    } catch {}
  };

  // Create unique members from referrals
  const memberMap = new Map();
  
  referrals.forEach(referral => {
    const key = referral.memberMedicaidId;
    if (!memberMap.has(key)) {
      memberMap.set(key, {
        id: referral.memberMedicaidId,
        name: referral.memberName,
        phone: referral.phoneNumber || "N/A",
        email: referral.email || "N/A",
        zipCode: referral.zipCode,
        dateOfBirth: referral.dateOfBirth,
        medicaidId: referral.memberMedicaidId,
        healthPlan: referral.healthPlan,
        lastActivity: referral.createdAt,
        status: referral.status === "Completed" ? "Completed" :
                referral.status === "On Hold" ? "On Hold" :
                referral.status === "In Progress" ? "In Progress" :
                referral.status === "Accepted" ? "Accepted" :
                referral.status === "Matched" ? "Matched" :
                referral.status === "auto-matched" ? "Matched" : // Auto-matched referrals show as Matched
                referral.status === "Pending" ? "Pending" : "Pending",
        referralCount: 0,
        referralIds: [],
        memberId: referral.memberId || referral.member_id,
      });
    }
    // Increment referral count and track referral IDs
    memberMap.get(key).referralCount++;
    memberMap.get(key).referralIds.push(referral.id);
  });

  const allMembers: Member[] = Array.from(memberMap.values());
  // Sort favorites to the top
  allMembers.sort((a, b) => {
    const aFav = favoriteIds.has(String(a.memberId || a.id));
    const bFav = favoriteIds.has(String(b.memberId || b.id));
    if (aFav && !bFav) return -1;
    if (!aFav && bFav) return 1;
    return 0;
  });

  // Filter members based on search query and status filter
  const members = allMembers.filter(m => {
    // Search filter - match name, medicaid ID, email, or phone
    const matchesSearch = searchQuery === '' ||
      m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.medicaidId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.phone.includes(searchQuery);

    // Status filter
    const matchesStatus = !statusFilter || m.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusVariant = (status: string): "default" | "success" | "warning" | "danger" | "info" => {
    switch (status) {
      case 'Pending':
        return 'warning';
      case 'Matched':
        return 'info';
      case 'Accepted':
        return 'info';
      case 'In Progress':
        return 'success';
      case 'On Hold':
        return 'warning';
      case 'Completed':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-sage-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            {!tabbed && <h1 className="text-4xl font-bold text-gray-900 mb-2">Member Profiles</h1>}
            <p className="text-lg text-gray-600">Manage and view member information and referral history</p>
          </div>
          {onBack && (
            <Button 
              onClick={onBack}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              <span>Back</span>
            </Button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <Card className="border border-blue-200/50 shadow-sm bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search by member name, Medicaid ID, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200 focus:border-blue-300"
                />
              </div>
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchQuery('')}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
            {(searchQuery || statusFilter) && (
              <div className="mt-3 text-sm text-gray-600">
                Showing <span className="font-semibold text-blue-600">{members.length}</span> of{' '}
                <span className="font-semibold">{allMembers.length}</span> members
                {searchQuery && <span className="ml-1">matching "{searchQuery}"</span>}
                {statusFilter && <span className="ml-1">with status "{statusFilter}"</span>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards - 6 cards in 2 rows (clickable filters) */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Card
          className={`cursor-pointer transition-all ${
            statusFilter === null
              ? 'bg-blue-50 border-2 border-blue-500 shadow-lg'
              : 'bg-white border border-blue-200/50 shadow-sm hover:shadow-md'
          }`}
          onClick={() => setStatusFilter(null)}
        >
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mb-2">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{allMembers.length}</p>
              <p className="text-xs text-gray-600">Total Members</p>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${
            statusFilter === 'Pending'
              ? 'bg-yellow-50 border-2 border-yellow-500 shadow-lg'
              : 'bg-white border border-yellow-200/50 shadow-sm hover:shadow-md'
          }`}
          onClick={() => setStatusFilter(statusFilter === 'Pending' ? null : 'Pending')}
        >
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center mb-2">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {allMembers.filter(m => m.status === 'Pending').length}
              </p>
              <p className="text-xs text-gray-600">Pending</p>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${
            statusFilter === 'Matched'
              ? 'bg-purple-50 border-2 border-purple-500 shadow-lg'
              : 'bg-white border border-purple-200/50 shadow-sm hover:shadow-md'
          }`}
          onClick={() => setStatusFilter(statusFilter === 'Matched' ? null : 'Matched')}
        >
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mb-2">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {allMembers.filter(m => m.status === 'Matched').length}
              </p>
              <p className="text-xs text-gray-600">Matched</p>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${
            statusFilter === 'Accepted'
              ? 'bg-blue-50 border-2 border-blue-500 shadow-lg'
              : 'bg-white border border-blue-200/50 shadow-sm hover:shadow-md'
          }`}
          onClick={() => setStatusFilter(statusFilter === 'Accepted' ? null : 'Accepted')}
        >
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mb-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {allMembers.filter(m => m.status === 'Accepted').length}
              </p>
              <p className="text-xs text-gray-600">Accepted</p>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${
            statusFilter === 'In Progress'
              ? 'bg-green-50 border-2 border-green-500 shadow-lg'
              : 'bg-white border border-green-200/50 shadow-sm hover:shadow-md'
          }`}
          onClick={() => setStatusFilter(statusFilter === 'In Progress' ? null : 'In Progress')}
        >
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {allMembers.filter(m => m.status === 'In Progress').length}
              </p>
              <p className="text-xs text-gray-600">In Progress</p>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${
            statusFilter === 'Completed'
              ? 'bg-teal-50 border-2 border-teal-500 shadow-lg'
              : 'bg-white border border-teal-200/50 shadow-sm hover:shadow-md'
          }`}
          onClick={() => setStatusFilter(statusFilter === 'Completed' ? null : 'Completed')}
        >
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center">
              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center mb-2">
                <CheckCircle className="w-5 h-5 text-teal-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {allMembers.filter(m => m.status === 'Completed').length}
              </p>
              <p className="text-xs text-gray-600">Completed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Favorites Summary */}
      {(() => {
        const favMembers = members.filter(m => favoriteIds.has(String(m.memberId || m.id)));
        if (favMembers.length === 0) return null;
        return (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              Favorite Members ({favMembers.length})
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {favMembers.map(m => (
                <div
                  key={`fav-${m.id}`}
                  onClick={() => onNavigateToWorkspace(m.id)}
                  className="flex-shrink-0 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 cursor-pointer hover:bg-yellow-100 transition-colors min-w-[200px]"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      {m.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{m.name}</p>
                      <p className="text-xs text-gray-500">{m.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <Badge variant={getStatusVariant(m.status)} size="sm">{m.status}</Badge>
                    <span className="text-xs text-gray-500">{m.referralCount} referrals</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Members List */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">All Members</h2>

        {members.length === 0 ? (
          <Card className="bg-white border border-gray-200/50 shadow-sm">
            <CardContent className="p-12 text-center">
              <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No members found</h3>
              <p className="text-gray-500 mb-4">
                {searchQuery
                  ? `No members match "${searchQuery}"`
                  : statusFilter
                    ? `No members with status "${statusFilter}"`
                    : 'No members available'}
              </p>
              {(searchQuery || statusFilter) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter(null);
                  }}
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear all filters
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {members.map((member) => (
            <Card 
              key={member.id}
              className="bg-white border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => onNavigateToWorkspace(member.id)}
            >
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={(e) => toggleFavorite(String(member.memberId || member.id), e)}
                      className="p-1 hover:scale-110 transition-transform"
                      title={favoriteIds.has(String(member.memberId || member.id)) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Star className={`w-5 h-5 ${
                        favoriteIds.has(String(member.memberId || member.id))
                          ? 'text-yellow-500 fill-yellow-500'
                          : 'text-gray-300 hover:text-yellow-400'
                      }`} />
                    </button>
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <CardTitle className="text-xl text-gray-900">{member.name}</CardTitle>
                      <p className="text-sm text-gray-600">Medicaid ID: {member.medicaidId}</p>
                    </div>
                  </div>
                  <Badge variant={getStatusVariant(member.status)} size="md">
                    {member.status}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span>{member.phone}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span>{member.email}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>{member.zipCode}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span>{member.dateOfBirth}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{member.referralCount}</span> referrals •
                    Last activity: <span className="font-medium">{member.lastActivity}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {onDeleteReferral && member.status === "Pending" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Are you sure you want to delete all ${member.referralCount} pending referral(s) for ${member.name}?`)) {
                            member.referralIds.forEach(id => onDeleteReferral(id));
                          }
                        }}
                        className="hover:bg-red-50 hover:border-red-200 text-red-600 transition-all duration-300 flex items-center space-x-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="group-hover:bg-blue-50 group-hover:border-blue-200 transition-all duration-300 flex items-center space-x-2"
                    >
                      <span>View Details</span>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        )}
      </div>
    </div>
  );
}

export default MemberProfiles;
